/**
 * GA4 Ecommerce Events — GTM dataLayer Module
 *
 * Pushes GA4 standard ecommerce events to window.dataLayer for Google Tag Manager.
 * GTM forwards these to GA4. No direct gtag() calls are used.
 *
 * All events follow the two-step GTM ecommerce pattern:
 *   1. Push { ecommerce: null }  — clears any previous ecommerce data
 *   2. Push { event, ecommerce } — the actual event payload
 *
 * Non-ecommerce events (login, sign_up, view_search_results) use a single push.
 *
 * Debug output via logger.js:
 *   Add ?debug=true to any URL to see grouped console output per event.
 *   Or: localStorage.setItem('debugLogging', 'true') for persistent logging across navigation.
 *
 * See: docs/analytics/GA4-DATALAYER-MIGRATION-PLAN.md for full implementation plan.
 */

import createLogger from '../../common/utils/logger';
import { setItemIdForVariant } from './ga4VariantSkuMap';

const log = createLogger('GA4Events');

// Allowed environment identifiers — events push in these environments.
// Includes dev/staging environments during migration to allow GTM Preview validation.
const ALLOWED_ENVS = new Set(['prod', 'production', 'local', 'sandbox', 'test']);

// ─────────────────────────────────────────────────────────────────────────────
// BREADCRUMB → GA4 CATEGORY PATH
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map a BigCommerce breadcrumb array to a single GA4 item_category path.
 * Skips the leading "Home" crumb (case-insensitive). Joins remaining names
 * with '/'. Returns null when no usable crumbs remain.
 *
 * No 5-level truncation: GA4's per-event item_category[N] cap doesn't apply
 * because the entire path now lives in a single item_category string field.
 *
 * @param {Array<{name: string}>} crumbs - e.g. window.LG_PAGE_DATA.breadcrumbs
 * @returns {string | null} e.g. "Jewelry Displays/Shop By Color/Slate Blue"
 */
export function mapBreadcrumbsToCategoryPath(crumbs) {
    if (!Array.isArray(crumbs) || !crumbs.length) return null;
    const names = crumbs
        .map(c => c?.name)
        .filter(n => typeof n === 'string' && n.trim() && n.toLowerCase() !== 'home');
    if (!names.length) return null;
    return names.join('/');
}

/**
 * Resolve the current page's breadcrumb into a GA4 item_category path string.
 * Convenience wrapper over mapBreadcrumbsToCategoryPath that reads
 * window.LG_PAGE_DATA.breadcrumbs (emitted by lg-page-data.html on every layout).
 *
 * @returns {string | null}
 */
export function getPageBreadcrumbCategoryPath() {
    return mapBreadcrumbsToCategoryPath(window.LG_PAGE_DATA?.breadcrumbs);
}

/**
 * Build the GA4 item_category* + page_subcategory subset of an item payload.
 *
 * - item_category:  breadcrumb names joined with '/'. Filters 'Home' (case-insensitive)
 *                   and (per productNameToStrip) drops the product name from the tail.
 * - item_category2..5: first 4 entries of productCategory, one per slot (GA4 caps at 5).
 *                   Entries that case-insensitively equal item_category are dropped first
 *                   to avoid duplicating the breadcrumb path; remaining entries shift up.
 * - page_subcategory: the last remaining breadcrumb name (the breadcrumb-leaf category).
 *
 * Empty slots are OMITTED from the returned object — no `item_categoryN: ''` keys.
 *
 * Strip rules for the breadcrumb tail:
 *   productNameToStrip === null    → always drop the last remaining crumb
 *   productNameToStrip is a string → drop only when last crumb matches case-insensitively
 *
 * @param {Array<{name: string}>} crumbs              - e.g. window.LG_PAGE_DATA.breadcrumbs
 * @param {Array<string>}         productCategory     - product.category or cartItem.category_names
 * @param {string|null}           productNameToStrip  - see strip rules above
 * @returns {Object} partial item: { item_category?, item_category2?..5?, page_subcategory? }
 */
export function buildGa4ItemCategoryFields(crumbs, productCategory, productNameToStrip) {
    const names = (Array.isArray(crumbs) ? crumbs : [])
        .map(c => c?.name)
        .filter(n => typeof n === 'string' && n.trim() && n.toLowerCase() !== 'home');

    if (names.length > 0) {
        const last = names[names.length - 1];
        const shouldStrip = productNameToStrip === null
            || (typeof productNameToStrip === 'string'
                && productNameToStrip !== ''
                && last.toLowerCase() === productNameToStrip.toLowerCase());
        if (shouldStrip) names.pop();
    }

    const fields = {};
    const itemCategory = names.join('/');
    if (itemCategory) fields.item_category = itemCategory;

    // Drop product.category entries that duplicate item_category (case-insensitive).
    // Remaining entries shift up to fill the gap. No-op when itemCategory is empty.
    const itemCategoryLower = itemCategory.toLowerCase();
    const cats = (Array.isArray(productCategory) ? productCategory : [])
        .filter(c => typeof c === 'string'
            && (itemCategoryLower === '' || c.toLowerCase() !== itemCategoryLower));

    for (let i = 0; i < 4; i++) {
        if (cats[i]) fields[`item_category${i + 2}`] = cats[i];
    }

    const pageSubcategory = names[names.length - 1];
    if (pageSubcategory) fields.page_subcategory = pageSubcategory;

    return fields;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAPPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize a BigCommerce cart item object to the GA4 items[] format.
 * Handles both the Stencil context format and the Storefront Cart API format.
 *
 * @param {Object} item - A cart item (context or Storefront Cart API)
 * @returns {Object} GA4-formatted item object
 */
function mapCartItemToGa4Item(item) {
    if (!item.sku) {
        log.warn('item missing sku — falling back to id:', item.id, item.name || item.product_name);
    }

    return {
        item_id:       item.product_sku || item.sku || String(item.productId || item.product_id || item.id || ''),
        item_name:     item.name || item.product_name || '',
        item_brand:    item.brand?.name || item.brand_name || '',
        item_category: item.category_names?.[0] || item.categoryNames?.[0] || '',
        item_variant:  item.sku || '',
        // Nested: Stencil context (sale_price.value), Storefront Cart API (salePrice object or number)
        // Flat:   BigCommerce itemRemove response (sale_price / base_price as plain numbers)
        price:         item.sale_price?.value
                        ?? item.list_price?.value
                        ?? item.salePrice?.value
                        ?? item.salePrice
                        ?? item.listPrice
                        ?? (typeof item.sale_price === 'number' ? item.sale_price : undefined)
                        ?? (typeof item.base_price === 'number' ? item.base_price : undefined)
                        ?? 0,
        quantity:      item.quantity || 1,
    };
}

/**
 * Normalize a BigCommerce product context object (this.context.product) to GA4 items[] format.
 * Used for view_item fired on PDP load.
 *
 * @param {Object} product - BigCommerce Stencil product context
 * @returns {Object} GA4-formatted item object
 */
function mapProductContextToGa4Item(product) {
    const categories = Array.isArray(product.category)
        ? product.category
        : (product.category ? [product.category] : []);

    const categoryFields = buildGa4ItemCategoryFields(
        window.LG_PAGE_DATA?.breadcrumbs,
        categories,
        null,
    );

    return {
        item_id:      product.product_sku || product.sku || String(product.id || ''),
        item_name:    product.title || product.name || '',
        item_brand:   product.brand?.name || '',
        ...categoryFields,
        item_variant: product.sku || '',
        price:        product.price?.without_tax?.value ?? product.price?.value ?? 0,
        quantity:     1,
    };
}

/**
 * Normalize a product card from the DOM (listing/category page) to GA4 items[] format.
 * Card objects are extracted from DOM data attributes by the listing page JS.
 *
 * @param {Object} card                   - Product card extracted from DOM
 * @param {string} [card.id]
 * @param {string} [card.name]
 * @param {string} [card.sku]
 * @param {number|string} [card.price]
 * @param {string} [card.brand]
 * @param {string} [card.category]        - Single-string category fallback (legacy)
 * @param {string} [card.categoryPath]    - Joined breadcrumb path string ("A/B/C") from
 *                                          getPageBreadcrumbCategoryPath; when set,
 *                                          becomes item_category. Overrides card.category.
 * @param {number} index                  - Card position (0-based)
 * @param {string} [itemListName]         - Final fallback for item_category.
 * @returns {Object} GA4-formatted item object
 */
export function mapCardToGa4Item(card, index = 0, itemListName) {
    return {
        item_id:       card.sku || String(card.id || ''),
        item_name:     card.name || '',
        item_brand:    card.brand || '',
        item_variant:  card.sku || '',
        price:         typeof card.price === 'number' ? card.price : parseFloat(card.price) || 0,
        quantity:      1,
        index,
        item_category: card.categoryPath || card.category || itemListName || '',
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// PUSH HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if the current environment is in the allowed list.
 * @returns {boolean}
 */
function isAllowedEnvOnly() {
    const env = window.BC_CONTEXT?.site_environment;
    if (!ALLOWED_ENVS.has(env)) {
        log.debug(`[SKIPPED — env is "${env}"]`);
        return false;
    }
    return true;
}

/**
 * Check if GA4 events are enabled (master toggle + environment).
 * @returns {boolean}
 */
function isAllowedEnv() {
    if (!window.BC_CONTEXT?.enable_ga4_events) {
        log.debug('[SKIPPED — GA4 events disabled via theme settings]');
        return false;
    }
    return isAllowedEnvOnly();
}

/**
 * Core ecommerce push helper — clears ecommerce, pushes the event, and logs it.
 *
 * @param {string}  eventName      - GA4 standard event name
 * @param {Object}  ecommerceData  - The ecommerce object payload
 * @param {boolean} bypassToggle   - If true, skip the enable_ga4_events check (env-only guard)
 */
function pushEcommerceEvent(eventName, ecommerceData, bypassToggle = false) {
    if (bypassToggle ? !isAllowedEnvOnly() : !isAllowedEnv()) return;

    window.dataLayer = window.dataLayer || [];

    // Required: clear previous ecommerce object before each push to prevent data bleed
    window.dataLayer.push({ ecommerce: null });

    const payload = { event: eventName, ecommerce: ecommerceData };
    window.dataLayer.push(payload);

    log.groupCollapsed(`${eventName} → dataLayer`);
    log.debug('payload:', payload);
    if (ecommerceData.items?.length) {
        log.table(ecommerceData.items, ['item_id', 'item_name', 'price', 'quantity']);
    }
    log.groupEnd();
}

/**
 * Simple (non-ecommerce) event push helper — pushes an { ecommerce: null }
 * reset, then the event. Used for login, sign_up, view_search_results, etc.
 *
 * @param {string} eventName - GA4 event name
 * @param {Object} params    - Event parameters
 */
function pushSimpleEvent(eventName, params = {}) {
    if (!isAllowedEnv()) return;

    window.dataLayer = window.dataLayer || [];

    const payload = { event: eventName, ...params };
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);

    log.groupCollapsed(`${eventName} → dataLayer`);
    log.debug('payload:', payload);
    log.groupEnd();
}

// ─────────────────────────────────────────────────────────────────────────────
// ECOMMERCE EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fire GA4 view_item event.
 * Hook location: assets/js/theme/product.js — onReady()
 *
 * @param {Object} product  - BigCommerce Stencil product context (this.context.product)
 * @param {string} currency - ISO 4217 code, default 'USD'
 */
export function fireViewItem(product, currency = 'USD') {
    if (!product || !product.id) {
        log.warn('fireViewItem called without product context — event not pushed');
        return;
    }

    const item = mapProductContextToGa4Item(product);

    pushEcommerceEvent('view_item', {
        currency,
        value: item.price,
        items: [item],
    });
}

// Dedupe state for fireViewItemList — SearchSpring's MutationObserver can re-invoke
// initListingClickTracking() with identical card contents during async grid rendering.
let _lastViewItemListSig = null;
let _lastViewItemListAt = 0;
const VIEW_ITEM_LIST_DEDUPE_MS = 2000;

/**
 * Fire GA4 view_item_list event.
 * Hook location: assets/js/theme/custom/event/listing-click-analytics.js — after cards render
 *
 * @param {Array}  cards    - Array of card objects: { id, name, sku, price, brand, category }
 * @param {string} listId   - Machine-readable list identifier (e.g. 'category_grooming')
 * @param {string} listName - Human-readable list name (e.g. 'Grooming Tools')
 * @param {string} currency - ISO 4217 code, default 'USD'
 */
export function fireViewItemList(cards, listId, listName, currency = 'USD') {
    if (!Array.isArray(cards) || !cards.length) {
        log.warn('fireViewItemList called with empty cards — event not pushed');
        return;
    }

    if (!window.BC_CONTEXT?.enable_ga4_view_item_list) {
        log.debug('[SKIPPED — view_item_list disabled via theme settings]');
        return;
    }

    const sig = `${listId}|${listName}|${cards.map(c => c.sku || c.id || '').join(',')}`;
    const now = Date.now();
    if (sig === _lastViewItemListSig && (now - _lastViewItemListAt) < VIEW_ITEM_LIST_DEDUPE_MS) {
        log.debug(`fireViewItemList deduped — identical signature within ${VIEW_ITEM_LIST_DEDUPE_MS}ms`);
        return;
    }
    _lastViewItemListSig = sig;
    _lastViewItemListAt = now;

    const items = cards.map((card, i) => ({
        ...mapCardToGa4Item(card, i,listName),
        item_list_id:   listId || '',
        item_list_name: listName || '',
    }));

    pushEcommerceEvent('view_item_list', {
        item_list_id:   listId || '',
        item_list_name: listName || '',
        items,
    });
}

/**
 * Fire GA4 select_item event.
 * Hook location: assets/js/theme/custom/event/listing-click-analytics.js — product card click handler
 *
 * @param {Object} card     - Card object: { id, name, sku, price, brand, category }
 * @param {string} listId   - Same list identifier used in view_item_list
 * @param {string} listName - Same list name used in view_item_list
 * @param {number} index    - 0-based position of the card in the list
 * @param {string} currency - ISO 4217 code, default 'USD'
 */
export function fireSelectItem(card, listId, listName, index = 0, currency = 'USD') {
    if (!card) {
        log.warn('fireSelectItem called without card data — event not pushed');
        return;
    }

    if (!window.BC_CONTEXT?.enable_ga4_select_item) {
        log.debug('[SKIPPED — select_item disabled via theme settings]');
        return;
    }

    const item = {
        ...mapCardToGa4Item(card, index, listName),
        item_list_id:   listId || '',
        item_list_name: listName || '',
    };

    pushEcommerceEvent('select_item', {
        item_list_id:   listId || '',
        item_list_name: listName || '',
        items: [item],
    });
}

/**
 * Fire GA4 add_to_cart event.
 * Hook location: assets/js/theme/common/product-details.js — itemAdd success callback
 *                assets/js/theme/f/card-add-to-cart.js — itemAdd success callback
 *
 * @param {Object} cartItem - Cart item from response.data.cart_item (BigCommerce itemAdd response)
 *                            Fields: { id, name, sku, quantity, sale_price, price_ex_tax, product_id }
 * @param {string} currency - ISO 4217 code, default 'USD'
 * @param {string} [listName] - Recommender the add came from (recommender-context.js);
 *                              sets item_list_name and a slugged item_list_id on the item.
 */
export function fireAddToCart(cartItem, currency = 'USD', listName = null) {
    if (!cartItem) {
        log.warn('fireAddToCart called without cartItem — event not pushed');
        return;
    }

    if (!isAllowedEnv()) return;

    const categories = Array.isArray(cartItem.category_names)
        ? cartItem.category_names
        : (cartItem.category_names ? [cartItem.category_names] : []);

    const categoryFields = buildGa4ItemCategoryFields(
        window.LG_PAGE_DATA?.breadcrumbs,
        categories,
        cartItem.name || '',
    );

    const item = {
        item_id:      cartItem.product_sku || String(cartItem.product_id || cartItem.id || ''),
        item_name:    cartItem.name || '',
        item_brand:   cartItem.brand_name || '',
        ...categoryFields,
        item_variant: cartItem.variant_sku ||  String(cartItem.product_id || cartItem.id || ''),
        price:        cartItem.price ?? cartItem.sale_price ?? cartItem.price_ex_tax ?? cartItem.price_inc_tax ?? 0,
        discount:     cartItem.discount ?? 0,
        quantity:     cartItem.quantity || 1,
    };
    if (listName) {
        item.item_list_id = listName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        item.item_list_name = listName;
    }

    setItemIdForVariant(item.item_variant, item.item_id);

    pushEcommerceEvent('add_to_cart', {
        currency,
        value: item.price * item.quantity,
        items: [item],
    });
}

/**
 * Fire GA4 remove_from_cart event.
 * Call this BEFORE removing the item so you still have its data.
 * Hook location: assets/js/theme/cart.js — before cartRemoveItem()
 *                assets/js/theme/f/side-cart.js — remove handler
 *
 * @param {Object} cartItem - Cart item being removed. Min: { name, price } — sku/id preferred.
 * @param {string} currency - ISO 4217 code, default 'USD'
 */
export function fireRemoveFromCart(cartItem, currency = 'USD') {
    if (!cartItem) {
        log.warn('fireRemoveFromCart called without cartItem — event not pushed');
        return;
    }

    const item = mapCartItemToGa4Item(cartItem);

    pushEcommerceEvent('remove_from_cart', {
        currency,
        value: item.price * item.quantity,
        items: [item],
    });
}

/**
 * Fire GA4 add_to_wishlist event (favorites star).
 * Hook location: assets/js/theme/f/favorites/favorites-ui.js after a successful add.
 *
 * @param {Object} fav  - { productId, variantId?, productName, listName? }
 * @param {string} currency
 */
export function fireAddToWishlist(fav, currency = 'USD') {
    if (!fav || fav.productId == null) {
        log.warn('fireAddToWishlist called without productId — event not pushed');
        return;
    }
    const item = { item_id: String(fav.productId), item_name: fav.productName || '' };
    if (fav.variantId != null && fav.variantId !== '') item.item_variant = String(fav.variantId);
    if (fav.listName) item.item_list_name = fav.listName;
    item.quantity = 1;

    pushEcommerceEvent('add_to_wishlist', { currency, items: [item] });
}

/**
 * Fire GA4 view_cart event.
 * Hook location: assets/js/theme/cart.js — onReady()
 *
 * @param {Array}  cartItems - From this.context.cartItems
 * @param {number} value     - Cart subtotal (from cart.sub_total.value)
 * @param {string} currency  - ISO 4217 code, default 'USD'
 */
export function fireViewCart(cartItems, value, currency = 'USD') {
    const items = Array.isArray(cartItems) ? cartItems.map(mapCartItemToGa4Item) : [];

    if (!items.length) {
        log.warn('fireViewCart called with empty cartItems — event not pushed');
        return;
    }

    pushEcommerceEvent('view_cart', {
        currency,
        value: typeof value === 'number' ? value : parseFloat(value) || 0,
        items,
    });
}

/**
 * Fire GA4 purchase event.
 * Call this on the order confirmation page with sessionStorage deduplication.
 * Note: Order confirmation uses empty.html layout (no webpack bundle), so this
 * function is NOT reachable from that page. See order-confirmation.html for the
 * inline implementation. This export is retained for any future webpack-accessible contexts.
 *
 * @param {Object} order    - Order data: { id, value, subtotal, tax, shipping, coupon, items[] }
 * @param {string} currency - ISO 4217 code, default 'USD'
 */
export function firePurchase(order, currency = 'USD') {
    if (!order || !order.id) {
        log.warn('firePurchase called without order.id — event not pushed');
        return;
    }

    // Deduplication guard — prevents double-fire on page refresh
    const dedupKey = `ga4_purchase_fired_${order.id}`;
    if (sessionStorage.getItem(dedupKey)) {
        log.debug(`firePurchase deduplicated for order ${order.id}`);
        return;
    }

    const items = Array.isArray(order.items) ? order.items.map(mapCartItemToGa4Item) : [];
    const value = typeof order.value === 'number' ? order.value : parseFloat(order.value) || 0;

    pushEcommerceEvent('purchase', {
        transaction_id: String(order.id),
        currency,
        value,
        subtotal: typeof order.subtotal === 'number' ? order.subtotal : parseFloat(order.subtotal) || 0,
        tax:      typeof order.tax === 'number' ? order.tax : parseFloat(order.tax) || 0,
        shipping: typeof order.shipping === 'number' ? order.shipping : parseFloat(order.shipping) || 0,
        ...(order.coupon ? { coupon: order.coupon } : {}),
        items,
    });

    try { sessionStorage.setItem(dedupKey, '1'); } catch (_) { /* storage unavailable */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// PROMOTION EVENTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fire GA4 view_promotion event.
 * Hook location: assets/js/theme/common/carousel.js — Slick afterChange + init
 *
 * @param {Array} promotions - Array of promotion objects:
 *   { promotion_id, promotion_name, creative_name, creative_slot }
 */
export function fireViewPromotion(promotions) {
    if (!Array.isArray(promotions) || !promotions.length) return;

    pushEcommerceEvent('view_promotion', {
        items: promotions,
    });
}

/**
 * Fire GA4 select_promotion event.
 * Hook location: assets/js/theme/common/carousel.js — slide click handler
 *
 * @param {Object} promotion - Single promotion object:
 *   { promotion_id, promotion_name, creative_name, creative_slot }
 */
export function fireSelectPromotion(promotion) {
    if (!promotion) return;

    pushEcommerceEvent('select_promotion', {
        items: [promotion],
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// NON-ECOMMERCE EVENTS
// ─────────────────────────────────────────────────────────────────────────────

// view_search_results fires once per query per browser-tab session, and only
// when the user is viewing the unfiltered, page-1 result set. Filter/sort/page
// changes and ss-on-results-load re-fires from listingPagesLoaded() are all
// suppressed by the URL predicate; F5/back/cross-call repeats are suppressed
// by the sessionStorage dedupe.
const VSR_SESSION_KEY_PREFIX = 'ga4_vsr_fired:';

function isUnfilteredFirstPageSearch(params) {
    const hasQuery = !!(params.get('search_query') || params.get('fallbackQuery'));
    if (!hasQuery) return false;
    const page = params.get('page');
    if (page && page !== '1') return false;
    // Suppress on `sort` param presence regardless of value — default sort means no param.
    if (params.has('sort')) return false;
    for (const key of params.keys()) {
        if (key.startsWith('filter.')) return false;
    }
    return true;
}

function hasFiredVsrThisSession(termKey) {
    try {
        return sessionStorage.getItem(VSR_SESSION_KEY_PREFIX + termKey) === '1';
    } catch (e) {
        log.debug('view_search_results sessionStorage read failed; treating as not-fired', e);
        return false;
    }
}

function markVsrFiredThisSession(termKey) {
    try {
        sessionStorage.setItem(VSR_SESSION_KEY_PREFIX + termKey, '1');
    } catch (e) {
        log.debug('view_search_results sessionStorage write failed; event will fire again on next call', e);
    }
}

/**
 * Fire GA4 view_search_results event.
 *
 * Gates (both must pass):
 *   1. URL represents an unfiltered, page-1 result load for a query
 *      (search_query or fallbackQuery present; no sort, no page>1, no filter.*).
 *   2. The normalized search term has not been fired this sessionStorage session.
 *
 * Zero-results convention: empty result sets still fire the event with
 * search_results_count: 0 (no separate no_search_results event).
 *
 * Hook locations:
 *   - assets/js/theme/search.js — onReady()
 *   - assets/js/theme/custom/event/listing-click-analytics.js — initListingClickTracking()
 *
 * @param {string} searchTerm   - The user's search query
 * @param {number} resultCount  - Number of results returned
 */
export function fireViewSearchResults(searchTerm, resultCount = 0) {
    const params = new URLSearchParams(window.location.search);
    if (!isUnfilteredFirstPageSearch(params)) {
        log.debug('fireViewSearchResults suppressed — filtered/sorted/paginated URL');
        return;
    }

    const termKey = (searchTerm || '').trim().toLowerCase();
    if (!termKey) {
        log.debug('fireViewSearchResults suppressed — empty search term');
        return;
    }

    if (hasFiredVsrThisSession(termKey)) {
        log.debug(`fireViewSearchResults deduped — "${termKey}" already fired this session`);
        return;
    }

    markVsrFiredThisSession(termKey);

    const count = typeof resultCount === 'number' ? resultCount : parseInt(resultCount, 10) || 0;
    pushSimpleEvent('view_search_results', {
        search_term: searchTerm || '',
        search_results_count: count,
    });
}

/**
 * Fire GA4 login event.
 * Hook location: assets/js/theme/auth.js — login form submit handler
 *
 * @param {string} method - Authentication method, e.g. 'email', 'google'
 */
export function fireLogin(method = 'email') {
    pushSimpleEvent('login', { method });
}

/**
 * Fire GA4 sign_up event.
 * Hook location: assets/js/theme/auth.js — create account form submit handler
 *
 * @param {string} method - Registration method, e.g. 'email'
 */
export function fireSignUp(method = 'email') {
    pushSimpleEvent('sign_up', { method });
}

// ─────────────────────────────────────────────────────────────────────────────
// LISTING page_view — fires on category/search AJAX state changes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strip the hash from a URL string.
 * @param {string} href
 * @returns {string}
 */
function stripHash(href) {
    const i = href.indexOf('#');
    return i === -1 ? href : href.slice(0, i);
}

/**
 * Resolve the current page_type, applying the BC /shop/ + search_query → 'search'
 * override that mirrors templates/components/scripts/ga4-datalayer-globals.html.
 *
 * Reads BC_CONTEXT.pageType (the raw Stencil {{page_type}} emitted by
 * init-globals.html) — NOT getPageType(), which transforms 'category' into
 * 'product_listing'/'search_results' and would diverge from the initial
 * GTM-fired page_view's value.
 *
 * @returns {string}
 */
function resolvePageType() {
    let pt = (window.BC_CONTEXT && window.BC_CONTEXT.pageType) || '';
    if (pt === 'category'
        && (location.pathname.indexOf('/shop/') !== -1
            || location.search.indexOf('search_query') !== -1)) {
        pt = 'search';
    }
    return pt;
}

/**
 * Resolve the page_name from the live DOM. Prefers the listing h1
 * (category__title for category/brand/SS search; page-heading for BC search),
 * falls back to document.title.
 * @returns {string}
 */
function resolvePageName() {
    const h1 = document.querySelector('h1.category__title, h1.page-heading');
    const fromH1 = h1?.textContent?.trim();
    if (fromH1) return fromH1;
    return document.title || '';
}

/**
 * Resolve page_category (second breadcrumb) and page_subcategory (last
 * non-Home crumb, case-insensitive) from window.LG_PAGE_DATA.breadcrumbs.
 * No PDP strip — this is listing-page only.
 * @returns {{ page_category: string, page_subcategory: string }}
 */
function resolveBreadcrumbFields() {
    const lgPageData = typeof LG_PAGE_DATA !== 'undefined' ? LG_PAGE_DATA : undefined;
    const crumbs = (lgPageData && lgPageData.breadcrumbs) || [];
    const page_category = crumbs[1] && crumbs[1].name ? crumbs[1].name : '';
    const nonHome = [];
    for (let i = 0; i < crumbs.length; i++) {
        const n = crumbs[i] && crumbs[i].name;
        if (typeof n === 'string' && n.trim() && n.toLowerCase() !== 'home') {
            nonHome.push(n);
        }
    }
    const page_subcategory = nonHome.length ? nonHome[nonHome.length - 1] : '';
    return { page_category, page_subcategory };
}

/**
 * Reserved (non-facet) URL params on listing pages. Anything not in this
 * set is treated as a facet and grouped into the returned object.
 */
const APPLIED_FILTER_RESERVED_KEYS = new Set([
    'page', 'sort', 'search_query', 'fallbackQuery', 'mode', 'limit',
]);

/**
 * Group URL params into a {facetName: [values]} object, excluding reserved
 * non-facet keys. URLSearchParams decodes values automatically.
 *
 * @param {URLSearchParams} params
 * @returns {Object<string, string[]>}
 */
export function deriveAppliedFilters(params) {
    const out = {};
    for (const [key, value] of params.entries()) {
        if (APPLIED_FILTER_RESERVED_KEYS.has(key)) continue;
        if (!out[key]) out[key] = [];
        out[key].push(value);
    }
    return out;
}

/**
 * Build the live page-context payload used by fireListingPageView.
 * Reads window.location, document, window.LG_PAGE_DATA, window.getPageType.
 * Hash is stripped from page_location and page_path.
 *
 * @returns {{
 *   page_type:        string,
 *   page_name:        string,
 *   page_category:    string,
 *   page_subcategory: string,
 *   page_location:    string,
 *   page_path:        string,
 *   page_title:       string,
 *   page_number:      number,
 *   sort_by:          string,
 *   applied_filters:  Object,
 * }}
 */
export function derivePageContext() {
    const params = new URLSearchParams(location.search);
    const { page_category, page_subcategory } = resolveBreadcrumbFields();
    return {
        page_type:        resolvePageType(),
        page_name:        resolvePageName(),
        page_category,
        page_subcategory,
        page_location:    stripHash(location.href),
        page_path:        (location.pathname || '') + (location.search || ''),
        page_title:       document.title || '',
        page_number:      parseInt(params.get('page'), 10) || 1,
        sort_by:          params.get('sort') || '',
        applied_filters:  deriveAppliedFilters(params),
    };
}

// Dedupe state for listing page_view — module-scoped, hash-stripped URL.
let _lastListingPageViewUrl = null;

/**
 * Reset module-scoped listing-page-view dedupe state. Test-only export.
 * @private
 */
export function __resetListingPageViewState() {
    _lastListingPageViewUrl = null;
}

/**
 * Globals from ga4-datalayer-globals.html that must be restated whenever
 * fireListingPageView re-pushes with `_clear: true`.
 *
 * GTM's data-layer v2 REPLACES the model on `_clear` rather than merging, so any global
 * not listed here is silently dropped for the rest of the page's SPA lifetime. The
 * page_* fields are deliberately absent: derivePageContext() owns those, and the
 * stashed copies are stale by the time this runs.
 */
const CLEAR_SAFE_GLOBAL_KEYS = [
    'loyalty_program',
    'customer_id',
    'customer_logged_in',
    'site_language',
    'site_currency',
    'site_environment',
];

/**
 * Pick the allowlisted user/site globals out of the object that
 * ga4-datalayer-globals.html actually pushed (window.GA4_GLOBALS).
 *
 * Reading the stash rather than re-deriving each value keeps the partial as the single
 * source of truth — a second derivation here would be a fourth mirror of logic already
 * triplicated (cf. TIG F-14, the breadcrumb fields).
 *
 * Presence is tested with `in`, not truthiness: the partial deliberately pushes
 * `customer_id: ''` for guests (TIG §3) and `customer_logged_in: false`, and both are
 * real values. Absent keys are omitted rather than set to `undefined`, because under
 * `_clear: true` an explicit `undefined` erases a good value instead of leaving it be.
 * The loop below also skips a key whose stashed value is itself `undefined`, for the
 * same reason — `in` is true for a key explicitly set to `undefined`, and copying that
 * through would erase a good value exactly as an omitted key would.
 *
 * Reads the bare global so the function works in the `node` Vitest environment, matching
 * resolveBreadcrumbFields above.
 *
 * @returns {Object} allowlisted globals, or `{}` when the stash is absent
 */
function pickClearSafeGlobals() {
    const stash = typeof GA4_GLOBALS !== 'undefined' ? GA4_GLOBALS : undefined;
    if (!stash || typeof stash !== 'object') return {};
    const out = {};
    for (const key of CLEAR_SAFE_GLOBAL_KEYS) {
        if (key in stash && stash[key] !== undefined) out[key] = stash[key];
    }
    return out;
}

/**
 * Fire a GA4 page_view for a listing AJAX state change (pagination, filter,
 * sort). Pushes the TIG §9.1 SPA sequence:
 *
 *   1. { ecommerce: null } reset.
 *   2. Refreshed page-context vars (no `event:` key) with _clear:true so
 *      GTM's data-layer v2 replaces rather than merges stale model state.
 *   3. { event: 'page_view', ...page-context fields } so GTM forwards
 *      a virtual page_view to GA4.
 *
 * Dedupe rules (module-scoped, hash-stripped URL):
 *   - First call seeds the dedupe URL and returns (initial page load —
 *     GTM's Google Tag already fired page_view).
 *   - Same-URL repeat calls (SS MutationObserver re-fires, etc.) return.
 *   - URL change pushes both messages and updates the dedupe URL.
 *
 * Honors the existing ALLOWED_ENVS + enable_ga4_events gate via
 * isAllowedEnv() before any push.
 *
 * Hook location: assets/js/theme/global/listingPagesLoaded.js —
 *                top of listingPagesLoaded() method body.
 */
export function fireListingPageView() {
    const currentUrl = stripHash(location.href);

    if (_lastListingPageViewUrl === null) {
        // First call = initial page load. GTM Google Tag already fired page_view.
        _lastListingPageViewUrl = currentUrl;
        log.debug('fireListingPageView: seeded dedupe URL, skipping initial push');
        return;
    }
    if (_lastListingPageViewUrl === currentUrl) {
        // Same URL as last push — listingPagesLoaded() can re-fire for the
        // same logical state (SS MutationObserver, swatch reload, etc.).
        log.debug('fireListingPageView: same URL, skipping');
        return;
    }
    _lastListingPageViewUrl = currentUrl;

    const ctx = derivePageContext();

    if (!isAllowedEnv()) return;
    window.dataLayer = window.dataLayer || [];

    // TIG §9.1 SPA sequence:
    //   1. ecommerce reset
    //   2. globals re-push with _clear so GTM's data-layer v2 REPLACES the
    //      model state instead of recursively merging over stale values
    //   3. virtual page_view event
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        _clear:           true,
        ...pickClearSafeGlobals(),
        page_type:        ctx.page_type,
        page_name:        ctx.page_name,
        ...(ctx.page_category ? { page_category: ctx.page_category } : {}),
        ...(ctx.page_subcategory ? { page_subcategory: ctx.page_subcategory } : {}),
    });
    const payload = {
        event:            'page_view',
        page_type:        ctx.page_type,
        page_name:        ctx.page_name,
        ...(ctx.page_category ? { page_category: ctx.page_category } : {}),
        ...(ctx.page_subcategory ? { page_subcategory: ctx.page_subcategory } : {}),
        page_location:    ctx.page_location,
        page_path:        ctx.page_path,
        page_title:       ctx.page_title,
        page_number:      ctx.page_number,
        ...(ctx.sort_by ? { sort_by: ctx.sort_by } : {}),
        applied_filters:  ctx.applied_filters,
    };
    window.dataLayer.push(payload);

    log.groupCollapsed('page_view (SPA) → dataLayer');
    log.debug('payload:', payload);
    log.groupEnd();
}
